// ABOUTME: Full-page user profile management component following PR 40 design
// ABOUTME: Provides comprehensive profile editing, password change, and avatar upload functionality

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useFamilyTerminology } from '../hooks/useFamilyTerminology';
import { useTheme } from '@mui/material/styles';
import naluV1Theme from '../theme/theme.nalu-v1';
import {
  Box,
  Typography,
  Paper,
  Avatar,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Container,
  IconButton,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  InputAdornment
} from '@mui/material';
import {
  Edit,
  Save,
  Cancel,
  Email,
  AdminPanelSettings,
  CalendarToday,
  CheckCircle,
  Group,
  NotificationsActive,
  Warning,
  Visibility,
  VisibilityOff
} from '@mui/icons-material';
import { getCurrentUserProfile, updateCurrentUserProfile, changeCurrentUserPassword, resendVerificationEmail } from '../services/api';
import api from '../services/api';
import { getAvatarUrl } from '../utils/avatarUtils';
import { formatUTCDateTime } from '../utils/dateUtils';
import AvatarUploadModal from './AvatarUploadModal';
import UserPasswordChangeModal from './UserPasswordChangeModal';
import AuthProviderManager from './AuthProviderManager';

interface UserProfile {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  profileImageUrl?: string;
  role?: string;
  mantra?: string;
  familyLoveQuestion?: string;
  familyNeedQuestion?: string;
  question1Answer?: string;
  question2Answer?: string;
  isAdmin: boolean;
  authProvider: string;
  emailVerificationStatus?: string;
  createdAt: string;
  updatedAt?: string;
  lastLogin?: string;
  families?: FamilyMembership[]; // Legacy field - kept for backward compatibility
  familyMemberships?: FamilyMembership[];
  totalFamilies?: number;
  totalChildren?: number;
}

interface FamilyMembership {
  familyId: number;
  familyName: string;
  systemRole: string; // "Owner" or "Adult"
  familyRelationship?: string; // "Mom", "Dad", etc.
  joinedAt: string;
  childrenCount: number;
  // Legacy field names, present when this object comes from the deprecated `families` array
  id?: number;
  name?: string;
  role?: string;
}

interface ProfileEditData {
  firstName: string;
  lastName: string;
  email: string;
  mantra: string;
  question1Answer: string;
  question2Answer: string;
}

const MyProfile: React.FC = () => {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const { term } = useFamilyTerminology();
  const theme = useTheme();
  const isNaluV1Theme = theme.logoSrc === naluV1Theme.logoSrc;
  const [user, setUser] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState<ProfileEditData>({
    firstName: '',
    lastName: '',
    email: '',
    mantra: '',
    question1Answer: '',
    question2Answer: '',
  });
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [avatarModalOpen, setAvatarModalOpen] = useState(false);
  const [passwordResetModalOpen, setPasswordResetModalOpen] = useState(false);
  const [resendingVerification, setResendingVerification] = useState(false);
  const [verificationMessage, setVerificationMessage] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0); // seconds remaining

  // Email change states
  const [pendingEmailChange, setPendingEmailChange] = useState<{
    newEmail: string;
    expiresAt: string;
    lastResendAt?: string;
  } | null>(null);
  const [isChangingEmail, setIsChangingEmail] = useState(false);
  const [emailChangePassword, setEmailChangePassword] = useState('');
  const [newEmailAddress, setNewEmailAddress] = useState('');
  const [emailChangeError, setEmailChangeError] = useState<string | null>(null);
  const [showEmailChangePassword, setShowEmailChangePassword] = useState(false);
  const [emailValidationError, setEmailValidationError] = useState<string | null>(null);

  useEffect(() => {
    fetchUserProfile();
  }, []);

  // Fetch pending email change on component mount
  useEffect(() => {
    const fetchPendingEmailChange = async () => {
      try {
        const response = await api.get<{
          newEmail: string;
          expiresAt: string;
          lastResendAt?: string;
        }>('/api/user/pending-email-change');
        if (response.status === 200) {
          setPendingEmailChange(response.data);
        }
      } catch {
        // No pending change or error - this is fine
      }
    };
    fetchPendingEmailChange();
  }, []);

  // Cooldown timer for resend verification
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown(resendCooldown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const fetchUserProfile = async () => {
    try {
      setLoading(true);
      setError(null);
      const userData = await getCurrentUserProfile() as UserProfile;
      setUser(userData);
      setEditData({
        firstName: userData.firstName || '',
        lastName: userData.lastName || '',
        email: userData.email || '',
        mantra: userData.mantra || '',
        question1Answer: userData.familyLoveQuestion || userData.question1Answer || '',
        question2Answer: userData.familyNeedQuestion || userData.question2Answer || '',
      });
    } catch (err) {
      setError('Failed to load profile. Please try again.');
      console.error('Error fetching user profile:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
    setSuccessMessage(null);
  };

  const handleCancel = () => {
    setIsEditing(false);
    if (user) {
      setEditData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        mantra: user.mantra || '',
        question1Answer: user.familyLoveQuestion || user.question1Answer || '',
        question2Answer: user.familyNeedQuestion || user.question2Answer || '',
      });
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      
      await updateCurrentUserProfile(editData);
      await fetchUserProfile();
      
      setIsEditing(false);
      setSuccessMessage('Profile updated successfully!');
      await refreshUser();
      
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      const responseMessage = axios.isAxiosError(err)
        ? (err.response?.data as { message?: string } | undefined)?.message
        : undefined;
      setError(responseMessage || (err instanceof Error ? err.message : String(err)) || 'Failed to save changes. Please try again.');
      console.error('Error saving profile:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setEditData({ ...editData, [field]: value });
  };

  const handlePasswordChange = async (currentPassword: string, newPassword: string) => {
    // Errors propagate to the caller — handled in the modal
    await changeCurrentUserPassword(currentPassword, newPassword);
    setPasswordResetModalOpen(false);
    setSuccessMessage('Password changed successfully!');
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  const handleAvatarUpdate = async (avatarUrl: string | null) => {
    try {
      if (avatarUrl) {
        // Upload the avatar
        const response = await api.post<{ avatarUrl: string }>('/api/user/avatar', {
          imageData: avatarUrl
        });
        const newAvatarUrl = response.data.avatarUrl;
        
        // Update user state
        setUser({ ...user!, profileImageUrl: newAvatarUrl });
        setSuccessMessage('Avatar updated successfully!');
      } else {
        // Remove the avatar
        await api.delete('/api/user/avatar');
        setUser({ ...user!, profileImageUrl: undefined });
        setSuccessMessage('Avatar removed successfully!');
      }
      
      // Refresh user context
      await refreshUser();
      setAvatarModalOpen(false);
      
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Error updating avatar:', err);
      setError('Failed to update avatar. Please try again.');
    }
  };

  const handleResendVerification = async () => {
    if (!user?.email || resendCooldown > 0) return;

    setResendingVerification(true);
    setVerificationMessage(null);

    try {
      const result = await resendVerificationEmail(user.email);
      setVerificationMessage(result.message || 'Verification email sent! Please check your inbox.');
      setResendCooldown(300); // 5 minutes = 300 seconds
      setTimeout(() => setVerificationMessage(null), 5000);
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 429) {
        setVerificationMessage((err.response?.data as { message?: string } | undefined)?.message || 'You can only send one email every 5 minutes. Please try again later.');
      } else {
        setVerificationMessage('Failed to send verification email. Please try again.');
      }
      setTimeout(() => setVerificationMessage(null), 5000);
    } finally {
      setResendingVerification(false);
    }
  };

  const formatCooldown = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Validate email format
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Handle email change request
  const handleRequestEmailChange = async () => {
    setEmailChangeError(null);
    setEmailValidationError(null);

    // Validate email format
    if (!validateEmail(newEmailAddress)) {
      setEmailValidationError('Please enter a valid email address');
      return;
    }

    try {
      const response = await api.post('/api/user/request-email-change', {
        newEmail: newEmailAddress,
        currentPassword: emailChangePassword
      });

      if (response.status === 200) {
        // Refresh pending change
        const pendingResponse = await api.get<{
          newEmail: string;
          expiresAt: string;
          lastResendAt?: string;
        }>('/api/user/pending-email-change');
        setPendingEmailChange(pendingResponse.data);
        setIsChangingEmail(false);
        setEmailChangePassword('');
        setNewEmailAddress('');
        setSuccessMessage('Email change requested! Check your new email address for verification link.');
        setTimeout(() => setSuccessMessage(null), 5000);
      }
    } catch (err) {
      // Status-code-specific error messages
      if (!axios.isAxiosError(err)) {
        setEmailChangeError('Failed to request email change. Please try again.');
      } else if (err.response?.status === 400) {
        setEmailChangeError('Invalid email format or email already in use');
      } else if (err.response?.status === 401) {
        setEmailChangeError('Password is incorrect');
      } else if (err.response?.status === 409) {
        setEmailChangeError('A pending email change already exists');
      } else if (!err.response) {
        setEmailChangeError('Network error. Please check your connection.');
      } else {
        setEmailChangeError((err.response?.data as { message?: string } | undefined)?.message || 'Failed to request email change. Please try again.');
      }
    }
  };

  // Handle cancel email change
  const handleCancelEmailChange = async () => {
    try {
      const response = await api.post('/api/user/cancel-email-change', {});

      if (response.status === 200) {
        setPendingEmailChange(null);
        setSuccessMessage('Email change request cancelled.');
        setTimeout(() => setSuccessMessage(null), 3000);
      }
    } catch {
      setError('Failed to cancel email change. Please try again.');
    }
  };

  // Handle resend verification for email change
  const handleResendEmailChangeVerification = async () => {
    try {
      const response = await api.post('/api/user/resend-email-change-verification');

      if (response.status === 429) {
        setEmailChangeError('Please wait before requesting another verification email.');
      } else if (response.status === 200) {
        setSuccessMessage('Verification email resent! Check your inbox.');
        setTimeout(() => setSuccessMessage(null), 3000);
      }
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 429) {
        setEmailChangeError((err.response?.data as { message?: string } | undefined)?.message || 'Please wait before requesting another verification email.');
      } else {
        setEmailChangeError('Failed to resend verification email. Please try again.');
      }
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error && !user) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">
          {error}
        </Alert>
      </Container>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>

      {/* Success/Error Messages */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {successMessage}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Email Verification Banner (Issue #986) */}
      {user.emailVerificationStatus !== 'Verified' && user.emailVerificationStatus !== 'VerificationSkipped' && (
        <Alert
          severity="warning"
          icon={<Warning />}
          sx={{ mb: 3 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={handleResendVerification}
              disabled={resendingVerification}
            >
              {resendingVerification ? 'Sending...' : 'Resend Verification Email'}
            </Button>
          }
        >
          <Typography variant="body2" fontWeight="bold">
            Email Not Verified
          </Typography>
          <Typography variant="body2">
            Your email address has not been verified. Please check your inbox for the verification email.
            {verificationMessage && (
              <Box component="span" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
                {verificationMessage}
              </Box>
            )}
          </Typography>
        </Alert>
      )}

      <Box display="flex" flexDirection={{ xs: 'column', lg: 'row' }} gap={isNaluV1Theme ? '20px' : 4}>
        {/* Profile Information */}
        <Box flex={{ xs: '1 1 auto', lg: '2 1 0' }}>
          <Paper sx={{ p: isNaluV1Theme ? '28px 32px' : 3, mb: isNaluV1Theme ? 0 : 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <Typography variant={isNaluV1Theme ? 'h4' : 'h5'} component="h2" sx={{ fontWeight: 600 }}>
                Profile Information
              </Typography>
              {!isEditing ? (
                <Button
                  variant="contained"
                  startIcon={<Edit />}
                  onClick={handleEdit}
                >
                  Edit Profile
                </Button>
              ) : (
                <Box gap={1} display="flex">
                  <Button
                    variant="outlined"
                    startIcon={<Cancel />}
                    onClick={handleCancel}
                    disabled={saving}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={saving ? <CircularProgress size={20} /> : <Save />}
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </Box>
              )}
            </Box>

            <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={3}>
              {/* Avatar Section */}
              <Box flex="0 0 auto" display="flex" flexDirection="column" alignItems="center">
                <Box position="relative" display="inline-block">
                  <Avatar
                    src={getAvatarUrl(user.profileImageUrl)}
                    alt={`${user.firstName} ${user.lastName} profile`}
                    sx={{ width: isNaluV1Theme ? 96 : 120, height: isNaluV1Theme ? 96 : 120 }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => setAvatarModalOpen(true)}
                    sx={{
                      position: 'absolute',
                      bottom: 4,
                      right: 4,
                      backgroundColor: 'primary.main',
                      // nalu-v1's primary.contrastText is explicitly black (white fails
                      // WCAG contrast on that palette's teal) — other themes keep white (issue #1900)
                      color: isNaluV1Theme ? 'primary.contrastText' : 'white',
                      width: 32,
                      height: 32,
                      '&:hover': {
                        backgroundColor: 'primary.dark',
                      },
                      boxShadow: 2
                    }}
                  >
                    {/* Icon glyph sizing via fontSize, not a Typography text override */}
                    <Edit sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
                {user.isAdmin && (
                  <Chip
                    label="Admin"
                    color="primary"
                    icon={<AdminPanelSettings />}
                    sx={{ mt: 2 }}
                  />
                )}
              </Box>

              {/* Form Fields */}
              <Box flex="1 1 auto">
                <Box display="flex" flexDirection="column" gap={2}>
                  <Box display="flex" gap={2} flexDirection={{ xs: 'column', sm: 'row' }}>
                    <TextField
                      label="First Name"
                      value={isEditing ? editData.firstName || '' : user.firstName || ''}
                      onChange={(e) => handleInputChange('firstName', e.target.value)}
                      disabled={!isEditing}
                      fullWidth
                      required
                    />
                    <TextField
                      label="Last Name"
                      value={isEditing ? editData.lastName || '' : user.lastName || ''}
                      onChange={(e) => handleInputChange('lastName', e.target.value)}
                      disabled={!isEditing}
                      fullWidth
                      required
                    />
                  </Box>
                  <TextField
                    label="Email"
                    type="email"
                    value={isEditing ? editData.email || '' : user.email || ''}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    disabled={!isEditing}
                    fullWidth
                    required
                  />
                  <TextField
                    label="Mantra"
                    value={isEditing ? editData.mantra || '' : user.mantra || ''}
                    onChange={(e) => handleInputChange('mantra', e.target.value)}
                    disabled={!isEditing}
                    fullWidth
                    multiline
                    rows={2}
                    placeholder="Your personal mantra or motto"
                  />
                  <TextField
                    label={`How does your ${term} know you love them?`}
                    value={isEditing ? editData.question1Answer || '' : user.familyLoveQuestion || user.question1Answer || ''}
                    onChange={(e) => handleInputChange('question1Answer', e.target.value)}
                    disabled={!isEditing}
                    fullWidth
                    multiline
                    rows={2}
                    placeholder="Share how you show love to your family..."
                  />
                  <TextField
                    label={`What do you need most from your ${term}?`}
                    value={isEditing ? editData.question2Answer || '' : user.familyNeedQuestion || user.question2Answer || ''}
                    onChange={(e) => handleInputChange('question2Answer', e.target.value)}
                    disabled={!isEditing}
                    fullWidth
                    multiline
                    rows={2}
                    placeholder={`What support do you need from your ${term}?`}
                  />
                </Box>
              </Box>
            </Box>
          </Paper>
        </Box>

        {/* Account Information */}
        <Box
          flex={{ xs: '1 1 auto', lg: '1 1 0' }}
          sx={isNaluV1Theme ? { display: 'flex', flexDirection: 'column', gap: '16px' } : undefined}
        >
          {/* Security Section */}
          <Paper sx={{ p: 3, mb: isNaluV1Theme ? 0 : 3 }}>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 600, mb: 3 }}>
              Security & Preferences
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => setPasswordResetModalOpen(true)}
                color="primary"
              >
                Change Password
              </Button>

              {/* Email Change Section */}
              {pendingEmailChange ? (
                <Box sx={{ p: 2, border: 1, borderColor: 'warning.main', borderRadius: isNaluV1Theme ? '12px' : 1 }}>
                  <Typography variant="subtitle2" color="warning.main" sx={{ mb: 1, fontWeight: 600 }}>
                    Pending Email Change
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    New email: <strong>{pendingEmailChange.newEmail}</strong>
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    Expires: {new Date(pendingEmailChange.expiresAt).toLocaleString()}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={handleResendEmailChangeVerification}
                    >
                      Resend Verification
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      onClick={handleCancelEmailChange}
                    >
                      Cancel Request
                    </Button>
                  </Box>
                  {emailChangeError && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      {emailChangeError}
                    </Alert>
                  )}
                </Box>
              ) : isChangingEmail ? (
                <Box sx={{ p: 2, border: 1, borderColor: 'primary.main', borderRadius: isNaluV1Theme ? '12px' : 1 }}>
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                    Change Email Address
                  </Typography>
                  <TextField
                    label="New Email Address"
                    type="email"
                    value={newEmailAddress}
                    onChange={(e) => {
                      setNewEmailAddress(e.target.value);
                      setEmailValidationError(null);
                    }}
                    error={!!emailValidationError}
                    helperText={emailValidationError}
                    fullWidth
                    size="small"
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    label="Current Password"
                    type={showEmailChangePassword ? 'text' : 'password'}
                    value={emailChangePassword}
                    onChange={(e) => setEmailChangePassword(e.target.value)}
                    fullWidth
                    size="small"
                    sx={{ mb: 2 }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowEmailChangePassword(!showEmailChangePassword)}
                            edge="end"
                            size="small"
                          >
                            {showEmailChangePassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  {emailChangeError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {emailChangeError}
                    </Alert>
                  )}
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={handleRequestEmailChange}
                      disabled={!newEmailAddress || !emailChangePassword}
                    >
                      Request Change
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        setIsChangingEmail(false);
                        setEmailChangePassword('');
                        setNewEmailAddress('');
                        setEmailChangeError(null);
                        setEmailValidationError(null);
                        setShowEmailChangePassword(false);
                      }}
                    >
                      Cancel
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Button
                  variant="outlined"
                  onClick={() => setIsChangingEmail(true)}
                  color="primary"
                >
                  Change Email Address
                </Button>
              )}

              <Button
                variant="outlined"
                onClick={() => navigate('/profile/email-preferences')}
                color="primary"
                startIcon={<NotificationsActive />}
              >
                Email Preferences
              </Button>
            </Box>
          </Paper>

          <Paper sx={{ p: 3, mb: isNaluV1Theme ? 0 : 3 }}>
            <Typography variant="h5" component="h2" sx={{ mb: 3, fontWeight: 600 }}>
              Account Information
            </Typography>

            <List>
              <ListItem>
                <ListItemIcon>
                  <Email />
                </ListItemIcon>
                <ListItemText
                  primary="Email Address"
                  secondary={
                    <Box>
                      <Typography variant="body2" component="span" display="block">
                        {user.email}
                      </Typography>
                      <Typography
                        variant="body2"
                        component="span"
                        display="block"
                        color={user.emailVerificationStatus === 'Verified' || user.emailVerificationStatus === 'VerificationSkipped' ? 'success.main' : 'warning.main'}
                        sx={{ fontWeight: 600 }}
                      >
                        {user.emailVerificationStatus === 'Verified' ? 'Verified' :
                         user.emailVerificationStatus === 'VerificationSkipped' ? 'Verification Skipped' :
                         'Not verified'}
                      </Typography>
                      {user.emailVerificationStatus !== 'Verified' && user.emailVerificationStatus !== 'VerificationSkipped' && (
                        <Box sx={{ mt: 1 }}>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={handleResendVerification}
                            disabled={resendingVerification || resendCooldown > 0}
                            sx={{ mr: 1 }}
                          >
                            {resendingVerification
                              ? 'Sending...'
                              : resendCooldown > 0
                                ? `Resend in ${formatCooldown(resendCooldown)}`
                                : 'Resend Verification Email'}
                          </Button>
                          {verificationMessage && (
                            <Typography
                              variant="body2"
                              component="span"
                              color={verificationMessage.includes('sent') ? 'success.main' : 'error.main'}
                              sx={{ fontStyle: 'italic' }}
                            >
                              {verificationMessage}
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Box>
                  }
                />
                {(user.emailVerificationStatus === 'Verified' || user.emailVerificationStatus === 'VerificationSkipped') && <CheckCircle color="success" />}
              </ListItem>

              {/* System Permissions Section */}
              {user.familyMemberships && user.familyMemberships.length > 0 && (
                <ListItem>
                  <ListItemIcon>
                    <AdminPanelSettings />
                  </ListItemIcon>
                  <ListItemText
                    primary="System Permissions"
                    secondary={
                      <Box>
                        {user.familyMemberships.map((membership) => (
                          <Typography key={membership.familyId} variant="body2" component="div">
                            <strong>{membership.familyName}:</strong> {membership.systemRole}
                          </Typography>
                        ))}
                      </Box>
                    }
                  />
                </ListItem>
              )}

              {/* Family Relationship Section */}
              {user.familyMemberships && user.familyMemberships.some(fm => fm.familyRelationship) && (
                <ListItem>
                  <ListItemIcon>
                    <Group />
                  </ListItemIcon>
                  <ListItemText
                    primary="Family Relationship"
                    secondary={
                      <Box>
                        {user.familyMemberships
                          .filter(fm => fm.familyRelationship)
                          .map((membership) => (
                            <Typography key={membership.familyId} variant="body2" component="div">
                              <strong>{membership.familyName}:</strong> {membership.familyRelationship}
                            </Typography>
                          ))}
                      </Box>
                    }
                  />
                </ListItem>
              )}

              <ListItem>
                <ListItemIcon>
                  <CalendarToday />
                </ListItemIcon>
                <ListItemText
                  primary="Account Created"
                  secondary={
                    user.createdAt ? (
                      <>
                        <Typography variant="body2" component="span" display="block">
                          {formatUTCDateTime(user.createdAt)?.date || 'Date not available'}
                        </Typography>
                        <Typography variant="body2" component="span" color="text.secondary" display="block">
                          {formatUTCDateTime(user.createdAt)?.time || ''}
                        </Typography>
                      </>
                    ) : (
                      'Not available'
                    )
                  }
                />
              </ListItem>

              {user.lastLogin && (
                <ListItem>
                  <ListItemIcon>
                    <CalendarToday />
                  </ListItemIcon>
                  <ListItemText
                    primary="Last Login"
                    secondary={
                      <>
                        <Typography variant="body2" component="span" display="block">
                          {formatUTCDateTime(user.lastLogin)?.date}
                        </Typography>
                        <Typography variant="body2" component="span" color="text.secondary" display="block">
                          {formatUTCDateTime(user.lastLogin)?.time}
                        </Typography>
                      </>
                    }
                  />
                </ListItem>
              )}
            </List>
          </Paper>

          {/* Family Memberships Section */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h5" component="h2" sx={{ mb: 3, fontWeight: 600 }}>
              Family Memberships
            </Typography>

            {((user.familyMemberships && user.familyMemberships.length > 0) || (user.families && user.families.length > 0)) ? (
              <>
                <List>
                  {/* Use new familyMemberships if available, otherwise fall back to legacy families */}
                  {(user.familyMemberships || user.families || []).map((membership: FamilyMembership) => {
                    // Handle both new and legacy data structures
                    const familyId = membership.familyId || membership.id;
                    const familyName = membership.familyName || membership.name;
                    // Prioritize family relationship (Dad, Mom) over system role (Owner, Adult)
                    const role = membership.familyRelationship || membership.systemRole || membership.role;
                    const childrenCount = membership.childrenCount;

                    return (
                      <ListItem key={familyId}>
                        <ListItemIcon>
                          <Group />
                        </ListItemIcon>
                        <ListItemText
                          primary={familyName}
                          secondary={
                            <>
                              <Typography variant="body2" component="span" display="block">
                                {role}
                              </Typography>
                              <Typography variant="body2" component="span" color="text.secondary" display="block">
                                {childrenCount} {childrenCount === 1 ? 'child' : 'children'}
                              </Typography>
                            </>
                          }
                        />
                      </ListItem>
                    );
                  })}
                </List>

                {(() => {
                  // Calculate totals from familyMemberships array (Phase 1 implementation)
                  const memberships = user.familyMemberships || user.families || [];
                  const familyCount = memberships.length;
                  const childrenCount = memberships.reduce((sum: number, membership: FamilyMembership) =>
                    sum + (membership.childrenCount || 0), 0
                  );

                  // Show summary only when user has 2+ families (smart display logic)
                  const shouldShowSummary = familyCount >= 2;

                  return shouldShowSummary ? (
                    <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                      <Typography variant="body2" color="text.secondary">
                        {familyCount} {familyCount === 1 ? 'family' : 'families'} • {childrenCount} {childrenCount === 1 ? 'child' : 'children'}
                      </Typography>
                    </Box>
                  ) : null;
                })()}
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No family memberships yet.
              </Typography>
            )}
          </Paper>

          {/* Authentication Providers Section */}
          <Box sx={{ mt: isNaluV1Theme ? 0 : 4 }}>
            <AuthProviderManager onUpdate={() => {
              // Refresh user data when auth providers change
              fetchUserProfile();
            }} />
          </Box>
        </Box>
      </Box>

      {/* Password Change Modal */}
      <UserPasswordChangeModal
        open={passwordResetModalOpen}
        onClose={() => setPasswordResetModalOpen(false)}
        onPasswordChange={handlePasswordChange}
      />

      {/* Avatar Upload Modal */}
      <AvatarUploadModal
        isOpen={avatarModalOpen}
        onClose={() => setAvatarModalOpen(false)}
        onSave={handleAvatarUpdate}
        currentAvatarUrl={user?.profileImageUrl}
      />
    </Container>
  );
};

export default MyProfile;